import Header from "@/components/unauthenticated-header";
import dynamic from "next/dynamic";
import UserFeedbackModal from "@/components/user-feedback-modal";
import UL_BottomBar from "@/components/user-layout-comps/ul-bottombar";
import UL_Header from "@/components/user-layout-comps/ul-header";
import UL_Sidebar from "@/components/user-layout-comps/ul-sidebar";
import {
  ROUTES,
  isCurrentPlaceDetailPath,
  isCurrentPlacePath,
  isCurrentPlayFeedPath,
  isCurrentPlayPath,
  normalizeLegacyPath,
} from "@/constants/routes";
import useEffectivePathname from "@/hooks/useEffectivePathname";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";

// 「장소 등록」 창은 열 때만 받아 온다.
// 이 창 안의 지도 위치 찍기 부품이 지도 라이브러리(1,095KB)를 끌고 오는데,
// 그것이 **모든 화면**의 첫 로딩에 얹혀 있었다(지도가 없는 /play 까지).
// 창을 여는 순간 받아 오면 되므로 화면은 똑같고 첫 로딩만 가벼워진다.
const PlaceAddModal = dynamic(
  () => import("@/components/places-comps/places-add-modal").then((m) => m.PlaceAddModal),
  { ssr: false }
);

const HIDDEN_LAYOUT_PATHS = [
  ROUTES.auth.forgotPassword,
  ROUTES.auth.resetPassword,
  "/retrieve-username",
];

const UserLayout = ({ children }) => {
  const pathname = useEffectivePathname();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isPlaceRegisterModalOpen, setIsPlaceRegisterModalOpen] =
    useState(false);

  const [isPending, startTransition] = useTransition();
  const [shouldHideLayout] = useState(
    HIDDEN_LAYOUT_PATHS.includes(pathname) ||
      pathname.startsWith(ROUTES.auth.resetPassword)
  );
  const isPlayPage = isCurrentPlayPath(pathname);
  const isPlayFeedPage = isCurrentPlayFeedPath(pathname);
  const isPlaceDetailPage = isCurrentPlaceDetailPath(pathname);
  // 앨범도 **상세 화면**이다 — 자기 상단바(‹ 앨범)를 이미 갖고 있는데 그 위에 전역
  //    상단바가 또 떠 있었고 하단 탭도 보였다(2026-08-10 검수). 플레이 상세와 같은 규칙.
  const isAlbumDetailPage = pathname?.startsWith("/album/");
  const hidePlayDetailMobileLayout =
    (isPlayPage && !isPlayFeedPage) || isAlbumDetailPage;
  const desktopShellClass = "contents";
  const mainClass =
    "min-w-0 flex-1 max-w-full max-h-full overflow-y-auto overflow-x-hidden flex flex-col relative";

  const router = useRouter();

  const handleFeedbackModalOpen = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackModalClose = () => {
    setIsFeedbackModalOpen(false);
  };

  const handlePlaceRegisterModalOpen = () => {
    setIsPlaceRegisterModalOpen(true);
  };

  const handlePlaceRegisterModalClose = () => {
    setIsPlaceRegisterModalOpen(false);
  };

  const routeTo = (target) => {
    const normalizedTarget = normalizeLegacyPath(target);
    if (!normalizedTarget) return;

    startTransition(() => {
      router.push(normalizedTarget);
    });
  };
  return (
    <div className="flex max-w-full flex-col overflow-x-hidden">
      {isPending && (
      <div className="hotple-loading-bar" role="progressbar" aria-label="불러오는 중">
        <div className="hotple-loading-bar-fill" />
      </div>
      )}
      <div className="flex h-[100svh] max-w-full overflow-x-hidden md:h-screen">
        <div className={desktopShellClass}>
          {!shouldHideLayout && (
            <UL_Sidebar
              routeTo={routeTo}
              handleFeedbackModalOpen={handleFeedbackModalOpen}
              handlePlaceRegisterModalOpen={handlePlaceRegisterModalOpen}
            />
          )}
          <div className={mainClass}>
            {(!isCurrentPlacePath(pathname) || isPlaceDetailPage) &&
              !pathname?.startsWith("/test") &&
              !shouldHideLayout && (
                <div className={hidePlayDetailMobileLayout ? "max-md:hidden" : ""}>
                  <UL_Header
                    handleFeedbackModalOpen={handleFeedbackModalOpen}
                    handlePlaceRegisterModalOpen={handlePlaceRegisterModalOpen}
                  />
                </div>
              )}
            {shouldHideLayout && <Header />}
            {children}
          </div>
        </div>
        {!shouldHideLayout && (
          <div className={hidePlayDetailMobileLayout ? "max-md:hidden" : ""}>
            <UL_BottomBar routeTo={routeTo} />
          </div>
        )}
      </div>
      {isFeedbackModalOpen && (
        <UserFeedbackModal onClose={handleFeedbackModalClose} />
      )}
      {isPlaceRegisterModalOpen && (
        <PlaceAddModal
          isOpen={isPlaceRegisterModalOpen}
          onClose={handlePlaceRegisterModalClose}
        />
      )}
    </div>
  );
};

export default UserLayout;
